import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useNavigate } from 'react-router-dom'
import type { State } from 'types/redux'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  changeMode,
  loadStats,
} from '@disco/state-management/actions/StatsActions'
import { ANALYTICS_UNLIMITED } from '@disco/constants/FeatureTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import StatsDateRange from 'components/common/stats-date-range'
import TrackName from 'components/common/track-name'
import DocumentHeadTags from 'components/document-head-tags'
import DataTable from 'components/ui/data-table'
import InfiniteScroll from 'components/ui/infinite-scroll'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'

const InsightsRecipientsTracksPlaybacks = (): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    endDate,
    entities: { tracks },
    startDate,
  }: State['stats'] = useSelector(state => state.stats)
  const isFeatureEnabled = useFeatureEnabled(ANALYTICS_UNLIMITED)
  useEffect(() => {
    if (!isFeatureEnabled) {
      navigate('/insights/recipients')
    }
  }, [isFeatureEnabled, navigate])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const loadData = useCallback(async () => {
    await dispatch(loadStats('tracks', 'playbacks'))
    setIsLoading(false)
  }, [dispatch])
  useEffect(() => {
    dispatch(changeMode('end-user'))
  }, [])
  useEffect(() => {
    if (isFeatureEnabled) {
      dispatch(sendMixpanelEvent('View more - Most streamed files'))
    }
  }, [isFeatureEnabled])
  useEffect(() => {
    if (isFeatureEnabled) {
      setIsLoading(true)
      loadData()
    }
  }, [endDate, isFeatureEnabled, startDate])
  const loadNext = useCallback(
    (next: string) => {
      dispatch(loadStats('tracks', 'playbacks', true))
    },
    [dispatch],
  )
  return (
    <>
      <PageHeader.Breadcrumbs
        breadcrumbs={{
          content: 'Back',
          link: '/insights/recipients',
        }}
      />
      <PageContainer>
        <PageContainer.Section
          title="Most streamed files"
          action={<StatsDateRange disabled={!isFeatureEnabled} />}
        >
          <DocumentHeadTags title="Most streamed files | DISCO" />
          <InfiniteScroll next={tracks.playbacks.next} loadNext={loadNext}>
            <DataTable
              appearance="plain"
              columns={[
                {
                  name: 'File',
                  render: ({ stats, ...track }: Record<string, any>) => (
                    <TrackName track={track as any} />
                  ),
                },
                {
                  name: 'Artist',
                  render: (item: Record<string, any>) => item.artist,
                },
                {
                  name: 'Streamed',
                  width: 'content',
                  render: (item: Record<string, any>) => item.stats.play_count,
                },
              ]}
              data={tracks.playbacks.data}
              loading={isLoading}
              rows={5}
            />
          </InfiniteScroll>
        </PageContainer.Section>
      </PageContainer>
    </>
  )
}

export default InsightsRecipientsTracksPlaybacks
